import React, { useState } from "react"
import { Col, Row, Spinner } from "react-bootstrap"
import { useSelector } from "react-redux"
import { Link, useHistory } from "react-router-dom"
import { toast } from "react-toastify"
import CustBreadcrumb from "components/CustBreadcrumb"
import { createAdminProduct, updateAdminProduct } from "requesters/productRequester"
import { getBaseUrl } from "apps/App/reducers"
import _ from "lodash"
import {
  FIELD_WORK_UPLOAD_OPTIONS,
  FILE_RELEASE_OPTIONS,
  OPERATIONS_CAN_BOOK_OPTIONS,
  STARTING_DIVISION_OPTIONS,
  WORKFLOW_OPTIONS,
  apiErrorMessage,
  productFormFromAttributes,
  withCurrentOption
} from "./productSettings"
import ProductReviewIcon from "./ProductReviewIcon"

const toastOptions = {
  toastId: "products-admin",
  containerId: "main"
}

export default function ProductForm({ product = null, onArchive, archiving = false }) {
  const history = useHistory()
  const baseUrl = useSelector(getBaseUrl)
  const [form, setForm] = useState(() => productFormFromAttributes(product?.attributes))
  const [saving, setSaving] = useState(false)

  const attributes = product?.attributes || {}
  const title = attributes.name || "New product"
  let archiveLabel = "Archive"
  if (attributes.archived) archiveLabel = "Restore"

  function update(field, value) {
    setForm(current => ({ ...current, [field]: value }))
  }

  async function handleSave() {
    if (!form.name.trim() || !form.code.trim()) {
      toast.error("Name and code are required", toastOptions)
      return
    }

    setSaving(true)
    const request = product
      ? updateAdminProduct(baseUrl, product.id, form)
      : createAdminProduct(baseUrl, form)
    const { response, error } = await request

    if (response) {
      toast.success(product ? "Product updated" : "Product added", toastOptions)
      if (!product) {
        const created = _.values(response.product || {})[0]
        if (created?.id) history.push(`/settings/products/${created.id}`)
        else history.push("/settings/products")
      }
    } else {
      console.error(error)
      toast.error(apiErrorMessage(error, "Failed to save product"), toastOptions)
    }
    setSaving(false)
  }

  return (
    <>
      <CustBreadcrumb
        links={[["Settings", "/settings"], ["Products", "/settings/products"]]}
        active={title} />
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">
          {title}
          <ProductReviewIcon id={product?.id || "new"} reasons={attributes.settingsReviewReasons} />
        </h2>
        <div className="d-flex align-items-center">
          {product && (
            <button className="btn btn-link mr-2" type="button" onClick={onArchive} disabled={archiving || !onArchive}>
              {archiving ? (
                <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
              ) : archiveLabel}
            </button>
          )}
          <Link className="btn btn-outlined-blue" to="/settings/products">
            Back to list
          </Link>
        </div>
      </div>
      {attributes.pipedriveProductId && (
        <p className="text-muted">{`Pipedrive ID ${attributes.pipedriveProductId}`}</p>
      )}
      <Row>
        <Col lg={8}>
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body p-4">
              <TextField label="Name" required value={form.name} onChange={value => update("name", value)} />
              <TextField label="Code" required value={form.code} onChange={value => update("code", value)} />
              <TextField label="Description" value={form.description} onChange={value => update("description", value)} as="textarea" rows={4} />
              <TextField label="Scope of work" value={form.scopeOfWork} onChange={value => update("scopeOfWork", value)} as="textarea" rows={6} />
              <SelectField
                label="Workflow"
                value={form.workflow}
                onChange={value => update("workflow", value)}
                options={withCurrentOption([{ value: "", label: "Not set" }, ...WORKFLOW_OPTIONS], form.workflow)} />
              <SelectField
                label="Starting division"
                value={form.startingDivision}
                onChange={value => update("startingDivision", value)}
                options={withCurrentOption(STARTING_DIVISION_OPTIONS, form.startingDivision)} />
              <TextField label="Estimated hours" type="number" value={form.estimatedHours} onChange={value => update("estimatedHours", value)} />
              <TextField label="Client due date count" value={form.clientDueDateCount} onChange={value => update("clientDueDateCount", value)} />
              <TextField label="Drafting due date count" value={form.draftingDueDateCount} onChange={value => update("draftingDueDateCount", value)} />
              <SelectField
                label="Operations can book / rebook"
                value={form.operationsCanBookRebook}
                onChange={value => update("operationsCanBookRebook", value)}
                options={withCurrentOption(OPERATIONS_CAN_BOOK_OPTIONS, form.operationsCanBookRebook)} />
              <SelectField
                label="Field work upload"
                value={form.fieldWorkUploadDaysCount}
                onChange={value => update("fieldWorkUploadDaysCount", value)}
                options={withCurrentOption(FIELD_WORK_UPLOAD_OPTIONS, form.fieldWorkUploadDaysCount)} />
              <SelectField
                label="Default file release"
                value={form.defaultFileRelease}
                onChange={value => update("defaultFileRelease", value)}
                options={withCurrentOption(FILE_RELEASE_OPTIONS, form.defaultFileRelease)} />
              <CheckField id="product-selectable" label="Selectable" checked={form.selectable} onChange={value => update("selectable", value)} />
              <CheckField id="product-dbyd" label="Dial Before You Dig required" checked={form.dbydRequired} onChange={value => update("dbydRequired", value)} />
              <CheckField id="product-active-flag" label="Active flag" checked={form.activeFlag} onChange={value => update("activeFlag", value)} />
            </div>
          </div>
          <button className="btn btn-red" type="button" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner animation="border" role="status" size="sm" /> : (product ? "Save" : "Add Product")}
          </button>
        </Col>
      </Row>
    </>
  )
}

function TextField({
  label, value, onChange, required = false, as = "input", rows, type = "text"
}) {
  const Input = as === "textarea" ? "textarea" : "input"
  return (
    <div className="mb-3">
      <label className="form-label">
        {label}
        {required ? " *" : ""}
      </label>
      <Input
        className="form-control"
        type={as === "textarea" ? undefined : type}
        rows={rows}
        value={value}
        onChange={event => onChange(event.target.value)} />
    </div>
  )
}

function SelectField({
  label, value, onChange, options
}) {
  return (
    <div className="mb-3">
      <label className="form-label">{label}</label>
      <select className="form-control" value={value || ""} onChange={event => onChange(event.target.value)}>
        {options.map(option => (
          <option key={option.value || "blank"} value={option.value}>{option.label}</option>
        ))}
      </select>
    </div>
  )
}

function CheckField({
  id, label, checked, onChange
}) {
  return (
    <div className="mb-3 form-check">
      <input
        id={id}
        className="form-check-input"
        type="checkbox"
        checked={checked}
        onChange={event => onChange(event.target.checked)} />
      <label className="form-check-label" htmlFor={id}>{label}</label>
    </div>
  )
}
